// src/pages/RegisterScreen.tsx — Phase 1 Slice B commit 3.
//
// POST /api/auth/register { email } per auth.md §5.1. Backend always returns
// { approved: boolean } with status 200 (no enumeration). UI follows: the
// success state is identical regardless of approved value, so an attacker
// probing for valid emails learns nothing — they always see the same
// "request submitted" Card.
//
// 2026-05-28 (UX gap fix): the post-Phase-10 flow routes unknown emails
// into hub_pending_registrations for admin approval (auth-pin.ts records
// the request rather than silently denying it). The pre-Phase-10 copy
// ("Check your email" / "we've sent you a PIN") was misleading because
// admin review happens before any PIN is emailed. Updated to spell out
// the approval step so users don't think the request failed.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import {
  AuthShell,
  BackLink,
  BrandMark,
  Button,
  Card,
  Eyebrow,
  Field,
  SuccessCard,
} from "../components/ui";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

export function RegisterScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });

  const trimmed = email.trim();
  const validEmail = EMAIL_REGEX.test(trimmed);

  async function submit() {
    if (state.kind === "submitting") return;
    if (!validEmail) {
      setState({ kind: "error", message: "Enter a valid email address." });
      return;
    }
    setState({ kind: "submitting" });
    try {
      await apiFetch<{ approved: boolean }>("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ email: trimmed }),
      });
      // Per auth.md §5.1 — same success state regardless of approved value.
      setState({ kind: "sent" });
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.";
      setState({ kind: "error", message: msg });
    }
  }

  return (
    <AuthShell>
      <div style={containerStyle}>
        <BrandMark />
        <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>
          Get started
        </Eyebrow>
        <h1 style={headerStyle}>Register for the Hub</h1>
        <p style={subStyle}>Request access to the staff hub.</p>

        {state.kind === "sent" ? (
          <SuccessCard
            tone="sage"
            title="Request submitted"
            body={
              <>
                <span style={{ display: "block" }}>
                  Thanks — an admin will review your request shortly. Once
                  approved, you&apos;ll receive your PIN by email and can sign in.
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: 10,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: C.inkMute,
                  }}
                >
                  If you don&apos;t hear back within a couple of days, contact your
                  NYMR admin.
                </span>
              </>
            }
            primaryLabel="Back to sign in"
            onPrimary={() => navigate("/login")}
          />
        ) : (
          <Card style={{ width: "100%", maxWidth: 380 }}>
            <Field
              label="Your personal email address"
              value={email}
              onChange={setEmail}
              type="email"
              ph="you@example.com"
              disabled={state.kind === "submitting"}
            />
            {state.kind === "error" && (
              <div role="alert" style={errorStyle}>
                {state.message}
              </div>
            )}
            <Button
              variant="primary"
              fullWidth
              onClick={submit}
              disabled={!validEmail || state.kind === "submitting"}
            >
              {state.kind === "submitting" ? "Sending…" : "Request access"}
            </Button>
            <BackLink onClick={() => navigate("/login")} />
          </Card>
        )}
      </div>
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 440,
  padding: "32px 4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const headerStyle: React.CSSProperties = {
  fontFamily: F.serif,
  fontSize: 28,
  fontWeight: 700,
  color: C.ink,
  margin: "4px 0 6px",
  letterSpacing: -0.6,
  textAlign: "center",
};

const subStyle: React.CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 14,
  color: C.inkSoft,
  marginTop: 0,
  marginBottom: 24,
  fontWeight: 600,
  textAlign: "center",
  maxWidth: 320,
};

const errorStyle: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 10,
  background: C.signalBg,
  border: "1px solid rgba(184,58,43,0.22)",
  color: C.signalDk,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 12,
  textAlign: "center",
};
